import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { userAtom } from '@root/entities/auth/atoms'
import {
  mfaEnabledAtom,
  userSessionsAtom,
} from '@root/entities/auth/atoms/reatom-user'
import { referralLinkAtom } from '@root/entities/referral/atoms'
import { removeSessionReatom } from '@root/features/auth/session-management/atoms'
import { WalletManagerRoute } from '@root/features/routing/model/app-route'
import { changeLanguageAction } from '@root/features/settings/settings-screen/atoms/languages'
import { getEmbeddedPolicyUrl } from '@root/features/settings/settings-screen/model'
import { environmentAtom } from '@root/shared'
import {
  languageInfosAtom,
  languageSelectionAtom,
} from '@root/shared/localization'
import { availableThemes, currentThemeIdAtom } from '@root/shared/theme'

import { PulseModal, PulseRow } from './pulse-modal'
import { PulseOtp, PulsePassword } from './pulse-security'

import type { Session } from '@root/entities/auth/model/session'

/**
 * Everything about this account the board can say for itself.
 *
 * The app's own settings modal still exists and still holds the long tail;
 * what is here is what belongs in front of somebody trading: what language
 * and theme they read in, which machines are signed in, where their wallets
 * and their invite link are, and how to clear a browser that has gone wrong.
 *
 * The two flows that take credentials — setting up an authenticator and
 * changing a password — stay in the app's screens. They are forms with
 * server round trips and their own error states, and redrawing them here
 * would be rewriting tested security UI for the sake of matching a palette.
 */
export const PulseSettings: React.FC<{ onClose: () => void }> = memo(
  ({ onClose }) => {
    const [user] = useAtom(userAtom)
    const [sessions] = useAtom(userSessionsAtom)
    const [mfaEnabled] = useAtom(mfaEnabledAtom)
    const [langs] = useAtom(languageInfosAtom)
    const [langSelection] = useAtom(languageSelectionAtom)
    const [themeId] = useAtom(currentThemeIdAtom)
    const [inviteLink] = useAtom(referralLinkAtom)
    const [env] = useAtom(environmentAtom)

    const setTheme = useAction(currentThemeIdAtom)
    const changeLanguage = useAction(changeLanguageAction)
    const navigate = useNavigate()

    const [showSessions, setShowSessions] = useState(false)
    const [step, setStep] = useState<'main' | 'otp' | 'password'>('main')

    const copy = (value: string | undefined) => () => {
      if (value) {
        navigator.clipboard.writeText(value).catch(() => undefined)
      }
    }

    const id = user?.delugeId

    if (step !== 'main') {
      return (
        <PulseModal
          title={
            step === 'otp' ? 'Two-factor authentication' : 'Change password'
          }
          onClose={onClose}
          onBack={() => setStep('main')}
          width={420}
        >
          {step === 'otp' ? (
            <PulseOtp onDone={() => setStep('main')} />
          ) : (
            <PulsePassword onDone={() => setStep('main')} />
          )}
        </PulseModal>
      )
    }

    return (
      <PulseModal title="Settings" onClose={onClose} width={520}>
        <PulseRow
          title={user?.username ? `@${user.username}` : 'Your account'}
          note={id ? `ID ${id.slice(0, 6)}…${id.slice(-4)}` : undefined}
        >
          <button className="act" type="button" onClick={copy(id)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2.5" />
              <path d="M15 6.5V6a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 6v6.5A2.5 2.5 0 0 0 6 15h.5" />
            </svg>
            Copy ID
          </button>
        </PulseRow>

        <PulseRow title="Language" note="What the terminal reads in.">
          <select
            className="pick"
            aria-label="Language"
            value={
              langSelection.type === 'manual' ? langSelection.lang : 'system'
            }
            onChange={(event) =>
              changeLanguage(
                event.target.value === 'system'
                  ? undefined
                  : (event.target.value as never),
              )
            }
          >
            <option value="system">Follow the system</option>
            {langs.map((info) => (
              <option key={info.lang} value={info.lang}>
                {info.localName}
              </option>
            ))}
          </select>
        </PulseRow>

        <PulseRow
          title="Theme"
          note="Applies to the rest of the terminal. This board keeps its own."
        >
          <select
            className="pick"
            aria-label="Theme"
            value={themeId}
            onChange={(event) => setTheme(event.target.value as never)}
          >
            {availableThemes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {themeName(theme.id)}
              </option>
            ))}
          </select>
        </PulseRow>

        <PulseRow
          title="Two-factor authentication"
          note={
            mfaEnabled
              ? 'On. A code from your authenticator is asked for on a new address.'
              : 'Off. Anyone with your password can sign in from anywhere.'
          }
        >
          <button className="act" type="button" onClick={() => setStep('otp')}>
            {mfaEnabled ? 'Manage' : 'Turn on'}
          </button>
        </PulseRow>

        <PulseRow title="Password" note="Changing it signs nobody else out.">
          <button
            className="act"
            type="button"
            onClick={() => setStep('password')}
          >
            Change
          </button>
        </PulseRow>

        <PulseRow
          title="Signed in on"
          note={`${sessions.length} ${sessions.length === 1 ? 'device' : 'devices'}. Revoking one ends it at once.`}
        >
          <button
            className="act"
            type="button"
            onClick={() => setShowSessions(!showSessions)}
          >
            {showSessions ? 'Hide' : 'Show'}
          </button>
        </PulseRow>

        {showSessions
          ? sessions.map((session) => (
              <SessionRow key={session.ip} session={session} />
            ))
          : undefined}

        <PulseRow title="Wallets" note="Add, import, export and transfer.">
          <button
            className="act"
            type="button"
            onClick={() => {
              onClose()
              navigate(WalletManagerRoute.path)
            }}
          >
            Manage
          </button>
        </PulseRow>

        <PulseRow
          title="Invite link"
          note={
            inviteLink
              ? 'Each friend who joins moves you up the queue.'
              : 'Available once your profile has loaded.'
          }
        >
          <button
            className="act"
            type="button"
            disabled={!inviteLink}
            onClick={copy(inviteLink?.toString())}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2.5" />
              <path d="M15 6.5V6a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 6v6.5A2.5 2.5 0 0 0 6 15h.5" />
            </svg>
            Copy
          </button>
        </PulseRow>

        <PulseRow
          title="Reset this browser"
          note="Clears what is kept here — board layout, filters, cached settings — and reloads. Your account is untouched."
          tone="warn"
        >
          <button
            className="act"
            type="button"
            data-tone="warn"
            onClick={() => {
              localStorage.clear()
              location.reload()
            }}
          >
            Reset
          </button>
        </PulseRow>

        <PulseRow title="Terms and privacy" note="What you agreed to.">
          <button
            className="act"
            type="button"
            onClick={() =>
              window.open(
                getEmbeddedPolicyUrl(env.policiesHost, 'terms-of-service'),
                '_blank',
                'noopener',
              )
            }
          >
            Terms
          </button>
          <button
            className="act"
            type="button"
            onClick={() =>
              window.open(
                getEmbeddedPolicyUrl(env.policiesHost, 'privacy-policy'),
                '_blank',
                'noopener',
              )
            }
          >
            Privacy
          </button>
        </PulseRow>
      </PulseModal>
    )
  },
)

/** Theme ids read as `Deluge::ThemeId`; the part before the marker is its name. */
const themeName = (id: string) => id.split('::')[0] ?? id

/** One signed-in machine, and the way to end it. */
const SessionRow: React.FC<{ session: Session }> = memo(({ session }) => {
  const model = useMemo(() => removeSessionReatom(session.ip), [session.ip])
  const revoke = useAction(model.removeSessionAction)

  const where = [session.browser, session.os].filter(Boolean).join(' · ')

  return (
    <PulseRow
      title={where || session.ip}
      note={`${session.ip} · last seen ${session.lastLogin.toLocaleDateString()}`}
    >
      {session.current ? (
        <span className="pick">This one</span>
      ) : (
        <button
          className="act"
          type="button"
          data-tone="warn"
          onClick={() => revoke()}
        >
          Revoke
        </button>
      )}
    </PulseRow>
  )
})
